import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  getModules,
  createModule,
  updateModule,
  deleteModule,
  Module,
  ModuleCreate,
} from '../../../services/rbac';
import { getModuleTableTheme, modernCellStyles } from '../../../theme/tableTheme';
import useAlert from '../../../hooks/useAlert';
import { extractErrorMessage } from '../../../utils/errorHandler';
import PermissionButton from '../../../components/PermissionButton';
import AlertDialog from '../../../components/AlertDialog';

const ModuleManagement: React.FC = () => {
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [formData, setFormData] = useState<ModuleCreate>({ name: '' });
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    setLoading(true);
    try {
      const data = await getModules();
      setModules(data);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (module?: Module) => {
    if (module) {
      setEditingModule(module);
      setFormData({ name: module.name });
    } else {
      setEditingModule(null);
      setFormData({ name: '' });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingModule(null);
    setFormData({ name: '' });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      showError('Le nom du module est requis');
      return;
    }

    try {
      if (editingModule) {
        await updateModule(editingModule.id, formData);
        showSuccess('Module modifié avec succès');
      } else {
        await createModule(formData);
        showSuccess('Module créé avec succès');
      }
      handleCloseDialog();
      fetchModules();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  const handleDelete = (module: Module) => {
    showConfirm(
      `Êtes-vous sûr de vouloir supprimer le module "${module.name}" ? Tous les sous-modules et profils associés seront également supprimés.`,
      async () => {
        try {
          await deleteModule(module.id);
          showSuccess('Module supprimé avec succès');
          fetchModules();
        } catch (error: any) {
          showError(extractErrorMessage(error));
        }
      },
      {
        title: 'Confirmer la suppression',
        confirmText: 'Supprimer',
      }
    );
  };

  const moduleColors: { [key: string]: string } = {
    Admin: '#F44336',
    Stock: '#2196F3',
    Config: '#1976D2',
    GRC: '#9C27B0',
    Prod: '#FF9800',
    Logistique: '#607D8B',
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            Gestion des Modules
          </Typography>
          <PermissionButton
            moduleName="Admin"
            operation="add"
            miniModuleName="Modules"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nouveau Module
          </PermissionButton>
        </Box>

        <TableContainer sx={getModuleTableTheme('administration')}>
          <Table>
            <TableHead>
              <TableRow>

                <TableCell sx={modernCellStyles}>Nom</TableCell>
                <TableCell sx={modernCellStyles}>Date de création</TableCell>
                <TableCell sx={modernCellStyles} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : modules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Aucun module trouvé
                  </TableCell>
                </TableRow>
              ) : (
                modules.map((module) => (
                  <TableRow key={module.id} hover>

                    <TableCell>
                      <Chip
                        label={module.name}
                        sx={{
                          bgcolor: moduleColors[module.name] || '#64748b',
                          color: 'white',
                        }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(module.created_at).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell align="right">
                      <PermissionButton
                        moduleName="Admin"
                        operation="edit"
                        miniModuleName="Modules"
                        component="IconButton"
                        onClick={() => handleOpenDialog(module)}
                        size="small"
                      >
                        <EditIcon />
                      </PermissionButton>
                      <PermissionButton
                        moduleName="Admin"
                        operation="delete"
                        miniModuleName="Modules"
                        component="IconButton"
                        onClick={() => handleDelete(module)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </PermissionButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingModule ? 'Modifier le module' : 'Nouveau module'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom du module"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            helperText="Ex: Admin, Stock, Config, GRC, Production..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingModule ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Dialog */}
      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default ModuleManagement;
